import { useLocation } from 'react-router-dom'
import { Link as RouterLink } from 'react-router-dom'
import { Link } from '@mui/material'
import { Breadcrumbs, Stack } from '@mui/material'
import HomeIcon from '@mui/icons-material/Home'

import { useDispatch, useSelector } from 'react-redux'
import { appActions as Actions } from '../../store/app'
import _ from 'lodash'

/**
 * Resolve a MongoDB ObjectID to a friendly display name using data already in Redux.
 * Checks the path segment before the ID to determine which resource list to search.
 */
const useIdResolver = () => {
  const facilitiesList = useSelector((state) => state.facilities_data?.facilitiesList?.data?.items)
  const residentsList = useSelector((state) => state.residents_data?.residentsList?.data?.items)
  const usersList = useSelector((state) => state.users_data?.usersList?.data?.items)

  // Also check single-item fetches (for when the list isn't loaded but the detail is)
  const getFacility = useSelector((state) => state.facilities_data?.getFacility?.data?.item)
  const getResident = useSelector((state) => state.residents_data?.getResidentProfile?.data?.item)
  const getUser = useSelector((state) => state.users_data?.getUser?.data?.item)

  return (id, parentSegment) => {
    if (!id || !id.match(/^[0-9a-fA-F]{24}$/)) return null

    switch (parentSegment) {
      case 'facilities': {
        const fromList = facilitiesList?.find((f) => f._id === id)
        if (fromList) return fromList.name
        if (getFacility?._id === id) return getFacility.name
        return null
      }
      case 'residents': {
        const fromList = residentsList?.find((r) => r._id === id)
        if (fromList) return fromList.fullName || `${fromList.firstName} ${fromList.lastName}`
        if (getResident?._id === id) return getResident.fullName || `${getResident.firstName} ${getResident.lastName}`
        return null
      }
      case 'users': {
        const fromList = usersList?.find((u) => u._id === id)
        if (fromList) return `${fromList.firstName} ${fromList.lastName}`.trim()
        if (getUser?._id === id) return `${getUser.firstName} ${getUser.lastName}`.trim()
        return null
      }
      default:
        return null
    }
  }
}

function Component() {
  const location = useLocation()
  const pathItems = _.compact(location.pathname.split('/'))
  const resolveId = useIdResolver()

  const dispatch = useDispatch()
  const itemHandler = (id) => {
    dispatch(Actions.menu({ openItem: [id] }))
  }

  const generateLink = (item) => {
    let path = ''
    for (let i = 0; i < pathItems.length; i++) {
      path = `${path}/${pathItems[i]}`
      if (pathItems[i] === item) {
        break
      }
    }
    return path
  }

  return (
    <Stack>
      <Breadcrumbs separator="/" aria-label="breadcrumbs">
        <Link
          onClick={() => itemHandler('/')}
          component={RouterLink}
          to="/"
          color="text.primary"
          style={{ textDecoration: 'none', display: 'flex', alignItems: 'center' }}
        >
          <HomeIcon sx={{ mr: 0.25, mb: 0.25, fontSize: '18px' }} />
          <span>Home</span>
        </Link>

        {pathItems.map((item, index) => {
          const isObjectId = item.match(/^[0-9a-fA-F]{24}$/)
          const parentSegment = index > 0 ? pathItems[index - 1] : null

          let linkItem
          if (isObjectId) {
            linkItem = resolveId(item, parentSegment) || '\u2026'
          } else {
            linkItem = _.upperFirst(_.lowerCase(item))
          }

          return (
            <Link
              key={item}
              onClick={() => itemHandler(item)}
              component={RouterLink}
              to={generateLink(item)}
              color="text.primary"
              style={{ textDecoration: 'none' }}
            >
              {linkItem}
            </Link>
          )
        })}
      </Breadcrumbs>
    </Stack>
  )
}

export default Component
